/**
 * NewslettersApi — Newsletter CRUD/bulk ops, WordPress publishing, image prompts, scheduling, and newsletter templates.
 *
 * Extracted from client.ts (CUR-547 god-object split). Methods-only by
 * design: prototype methods are merged onto AdminAPIClient via applyMixins
 * in client.ts, so the flat `apiClient.method()` surface is unchanged.
 * Instance state lives in BaseApiClient — do not add fields here.
 */

import type { AudiencePersona, CreateTemplateRequest, Newsletter, NewsletterGenerationRequest, NewsletterGenerationResponse, NewsletterImage, NewsletterListParams, NewsletterListResponse, NewsletterStatsResponse, NewsletterTemplate, NewsletterUpdateRequest, TemplateDeleteResponse, TemplateFormatUpdateRequest, VisualConfig } from '../../../../shared/types/api';
import { APIError as SharedAPIError, buildApiUrl, createAuthHeaders, processApiResponse } from '../../../../shared/utils/api-helpers';
import { API_ENDPOINTS } from '@/utils/env';

import { BaseApiClient } from './base';

export class NewslettersApi extends BaseApiClient {
  // ============================================================================
  // Newsletter Methods
  // ============================================================================

  async getNewsletters(params: NewsletterListParams = {}): Promise<NewsletterListResponse> {
    const url = buildApiUrl(this.baseUrl, '/api/newsletters', params);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<NewsletterListResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getNewsletter(id: number): Promise<Newsletter> {
    const url = buildApiUrl(this.baseUrl, `/api/newsletters/${id}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<Newsletter>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Fetch the rendered newsletter HTML with the admin's auth header. An
   * iframe `src` navigation cannot send Authorization, so the caller renders
   * the returned string through `srcDoc` instead.
   */
  async getNewsletterHtml(id: number): Promise<string> {
    const url = buildApiUrl(this.baseUrl, `/api/newsletters/${id}/html`);
    const headers = createAuthHeaders(await this.resolveToken());
    headers.Accept = 'text/html';

    const response = await fetch(url, { headers });
    const body = await response.text();

    if (!response.ok) {
      let message = `HTTP ${response.status}: ${response.statusText}`;
      try {
        message = JSON.parse(body).detail ?? message;
      } catch {
        // non-JSON error body; keep the status line
      }
      throw new SharedAPIError(response.status, message, response.status.toString());
    }

    return body;
  }

  async generateNewsletter(request: NewsletterGenerationRequest): Promise<NewsletterGenerationResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/newsletters/generate');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    const result = await processApiResponse<NewsletterGenerationResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async updateNewsletterStatus(id: number, updateData: NewsletterUpdateRequest): Promise<any> {
    // Admin endpoint expects query parameters, not JSON body
    const params: Record<string, any> = {};
    if (updateData.status) params.status = updateData.status;
    if (updateData.title) params.title = updateData.title;
    if (updateData.summary) params.summary = updateData.summary;

    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/status`, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'PUT',
      headers,
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async deleteNewsletter(id: number): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getNewsletterStats(): Promise<NewsletterStatsResponse> {
    const url = buildApiUrl(this.baseUrl, '/admin/newsletters/stats');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<NewsletterStatsResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getNewsletterHealth(): Promise<any> {
    const url = buildApiUrl(this.baseUrl, '/health/newsletter');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async getGenerationProgress(taskId: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/tasks/${taskId}/progress`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // Bulk newsletter operations
  async bulkNewsletterOperation(operation: {
    newsletter_ids: number[];
    operation: 'delete' | 'archive' | 'publish' | 'draft' | 'review';
    confirm: boolean;
  }): Promise<any> {
    const url = buildApiUrl(this.baseUrl, '/admin/newsletters/bulk-operation');
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(operation),
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  async searchNewsletters(_params: any): Promise<any> {
    throw new Error('Newsletter search not yet implemented');
  }

  async getNewsletterAnalytics(_period: string): Promise<any> {
    throw new Error('Newsletter analytics not yet implemented');
  }

  async exportNewsletter(_id: number, _format: string): Promise<any> {
    throw new Error('Newsletter export not yet implemented');
  }

  async validateNewsletter(_id: number): Promise<any> {
    throw new Error('Newsletter validation not yet implemented');
  }

  async getNewsletterPreview(_id: number): Promise<any> {
    throw new Error('Newsletter preview not yet implemented');
  }

  async getNewsletterImage(id: number): Promise<NewsletterImage> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/images`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<any>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    // Return first image if available
    const data = result.data!;
    if (data.images && data.images.length > 0) {
      return {
        image_url: data.images[0].s3_url,
        newsletter_id: data.newsletter_id,
        ...data.images[0]
      };
    }

    // Check if image is being generated
    if (data.generation_status === 'generating') {
      return {
        newsletter_id: data.newsletter_id,
        image_url: null,
        generation_status: 'generating',
        task_id: data.task_id,
        message: data.message || 'Image is currently being generated'
      } as NewsletterImage;
    }

    // No images available - return null image instead of throwing error
    // This allows the UI to show a placeholder icon gracefully
    return {
      newsletter_id: data.newsletter_id,
      image_url: null,
      generation_status: data.generation_status || 'not_started',
      message: data.message || 'No images available'
    } as NewsletterImage;
  }

  // ============================================================================
  // WordPress Publishing Methods
  // ============================================================================

  /**
   * Get publishing status for a newsletter (both Ghost and WordPress)
   */
  async getNewsletterPublishingStatus(id: number): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/publishing-status`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Update publishing flags for a newsletter (Ghost, WordPress, and/or Substack)
   */
  async updateNewsletterPublishingFlags(
    id: number,
    flags: {
      publish_to_ghost?: boolean;
      publish_to_wordpress?: boolean;
      publish_to_substack?: boolean;
    }
  ): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/publishing-flags`);
    const headers = createAuthHeaders(await this.resolveToken());

    // Send flags directly in the body (not wrapped in a "flags" object)
    console.log('🚀 API Client: Making PUT request:', {
      url,
      method: 'PUT',
      body: flags,
      headers: { ...headers, Authorization: '[REDACTED]' },
    });

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(flags),
    });

    console.log('📥 API Client: Response received:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    const result = await processApiResponse(response);

    console.log('📦 API Client: Processed result:', result);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Publish a newsletter to WordPress
   */
  async publishNewsletterToWordPress(
    id: number,
    options: {
      force_republish?: boolean;
    } = {}
  ): Promise<any> {
    const params: Record<string, any> = {};
    if (options.force_republish !== undefined) {
      params.force_republish = options.force_republish;
    }

    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/publish/wordpress`, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Publish a newsletter to Substack
   */
  async publishNewsletterToSubstack(
    id: number,
    options: {
      force_republish?: boolean;
    } = {}
  ): Promise<any> {
    const params: Record<string, any> = {};
    if (options.force_republish !== undefined) {
      params.force_republish = options.force_republish;
    }

    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/publish/substack`, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Publish a newsletter to all enabled platforms (Ghost + WordPress + Substack)
   */
  async publishNewsletterMultiPlatform(
    id: number,
    options: {
      force_republish?: boolean;
    } = {}
  ): Promise<any> {
    const params: Record<string, any> = {};
    if (options.force_republish !== undefined) {
      params.force_republish = options.force_republish;
    }

    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/publish/multi-platform`, params);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Image Prompt Methods (Phase 2: Image Prompt Tweaking)
  // ============================================================================

  /**
   * Get image prompt and history for a newsletter
   */
  async getNewsletterImagePrompt(id: number): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/image-prompt`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Regenerate newsletter image with custom prompt
   */
  async regenerateNewsletterImage(id: number, customPrompt: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/image-prompt/regenerate`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ custom_prompt: customPrompt }),
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Newsletter Scheduling Methods (Phase 3: Newsletter Scheduling)
  // ============================================================================

  /**
   * Schedule a newsletter for future publication
   */
  async scheduleNewsletter(id: number, scheduledAt: string, timezone: string): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/schedule`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ scheduled_at: scheduledAt, timezone }),
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Cancel a scheduled newsletter publication
   */
  async unscheduleNewsletter(id: number): Promise<any> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletters/${id}/unschedule`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  // ============================================================================
  // Newsletter Template Methods (Phase 2)
  // ============================================================================

  /**
   * Get list of newsletter templates with optional filtering
   */
  async getNewsletterTemplates(params: {
    limit?: number;
    offset?: number;
    domain?: string;
    cadence?: string;
    active?: boolean;
  } = {}): Promise<any> {
    const queryParams: Record<string, any> = {};
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.offset !== undefined) queryParams.offset = params.offset;
    if (params.domain !== undefined) queryParams.domain = params.domain;
    if (params.cadence !== undefined) queryParams.cadence = params.cadence;
    if (params.active !== undefined) queryParams.active = params.active;

    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplates, queryParams);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get a single newsletter template by ID
   */
  async getNewsletterTemplate(id: number): Promise<any> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplate(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Update template prompts and content configuration
   */
  async updateTemplatePrompts(
    id: number,
    prompts: {
      story_selection_prompt?: string;
      synthesis_prompt?: string;
      writer_prompt?: string;
      content_types?: string[];
    }
  ): Promise<any> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplatePrompts(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(prompts),
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Test newsletter generation with a template
   */
  async testTemplateGeneration(
    id: number,
    options: {
      force_generation?: boolean;
      dry_run?: boolean;
    } = {}
  ): Promise<any> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplateTest(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        template_id: id,
        ...options,
      }),
    });

    const result = await processApiResponse(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Generate Top 3 newsletter from a multi-domain template
   */
  async generateTop3Newsletter(templateId: number): Promise<{
    success: boolean;
    html_content?: string;
    newsletter?: {
      total_videos?: number;
      domains_covered?: string[];
    };
    error?: string;
  }> {
    const url = buildApiUrl(this.baseUrl, `/admin/newsletter-templates/${templateId}/generate-top3`);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    const result = await processApiResponse<{
      success: boolean;
      html_content?: string;
      newsletter?: {
        total_videos?: number;
        domains_covered?: string[];
      };
      error?: string;
    }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get baseline prompts for a format type
   */
  async getBaselinePrompts(formatType: 'single_domain' | 'multi_domain' | 'visual_card' = 'single_domain'): Promise<{
    format_type: 'single_domain' | 'multi_domain' | 'visual_card';
    available_formats: string[];
    prompts: {
      story_selection_prompt: string;
      synthesis_prompt: string;
      writer_prompt: string;
    };
    prompt_lengths: {
      story_selection_prompt: number;
      synthesis_prompt: number;
      writer_prompt: number;
    };
    timestamp: string;
  }> {
    const url = buildApiUrl(
      this.baseUrl,
      `${API_ENDPOINTS.newsletterTemplateBaselinePrompts}?format_type=${formatType}`
    );
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, { headers });
    const result = await processApiResponse<{
      format_type: 'single_domain' | 'multi_domain';
      available_formats: string[];
      prompts: {
        story_selection_prompt: string;
        synthesis_prompt: string;
        writer_prompt: string;
      };
      prompt_lengths: {
        story_selection_prompt: number;
        synthesis_prompt: number;
        writer_prompt: number;
      };
      timestamp: string;
    }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Create a new newsletter template
   */
  async createNewsletterTemplate(data: CreateTemplateRequest): Promise<NewsletterTemplate> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplates);
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    const result = await processApiResponse<NewsletterTemplate>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Delete (soft delete) a newsletter template
   */
  async deleteNewsletterTemplate(id: number): Promise<TemplateDeleteResponse> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplate(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    const result = await processApiResponse<TemplateDeleteResponse>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Update template audience persona (for Top 3 Things format)
   */
  async updateTemplatePersona(
    id: number,
    persona: AudiencePersona
  ): Promise<NewsletterTemplate> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplatePersona(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ audience_persona: persona }),
    });

    const result = await processApiResponse<NewsletterTemplate>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Update template format configuration (for multi-domain support)
   */
  async updateTemplateFormat(
    id: number,
    formatConfig: TemplateFormatUpdateRequest
  ): Promise<NewsletterTemplate> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplateFormat(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(formatConfig),
    });

    const result = await processApiResponse<NewsletterTemplate>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Update template visual configuration (section names, colors)
   */
  async updateTemplateVisualConfig(
    id: number,
    visualConfig: VisualConfig
  ): Promise<NewsletterTemplate> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.newsletterTemplateVisualConfig(id));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ visual_config: visualConfig }),
    });

    const result = await processApiResponse<NewsletterTemplate>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

  /**
   * Get domain section defaults from domain_config registry
   */
  async getDomainSections(domain: string): Promise<{
    domain: string;
    display_name: string;
    primary_color: string;
    accent_color: string;
    section_names: Record<string, string>;
    timestamp: string;
  }> {
    const url = buildApiUrl(this.baseUrl, API_ENDPOINTS.domainSections(domain));
    const headers = createAuthHeaders(await this.resolveToken());

    const response = await fetch(url, {
      headers,
    });

    const result = await processApiResponse<{
      domain: string;
      display_name: string;
      primary_color: string;
      accent_color: string;
      section_names: Record<string, string>;
      timestamp: string;
    }>(response);

    if (result.error) {
      throw new SharedAPIError(result.status, result.error.message, result.error.code);
    }

    return result.data!;
  }

}
